const KPI_CARDS = [
  {
    icon: 'play_circle',
    label: 'Video Completion Rate (VCR)',
    value: '64.2%',
    delta: '+12.4%',
  },
  {
    icon: 'qr_code_2',
    label: 'QR Scans',
    value: '1,240',
    delta: '+5.2%',
  },
  {
    icon: 'data_thresholding',
    label: 'First-Quartile Drop-Off',
    value: '12.8%',
    delta: '+18.1%',
  },
];

const CAMPAIGN_ROWS = [
  { name: 'Summer Glow Launch', status: 'Active', qr: '850', vcr: '75.3%' },
  { name: 'Holiday Omni-Channel', status: 'Scheduled', qr: '620', vcr: '61.2%' },
  { name: 'Skincare Refresh', status: 'Active', qr: '1,140', vcr: '82.5%' },
];

const LEADERBOARD = [
  { rank: 1, name: 'Golden Hour Serum', scans: '980', pct: 95 },
  { rank: 2, name: 'Summer Dew Mist', scans: '745', pct: 78 },
  { rank: 3, name: 'Night Repair Elixir', scans: '610', pct: 65 },
];

const AI_INSIGHTS = [
  {
    metric: 'QR Scans',
    body: 'The Social eComm template is the best performing creative to drive action — up to 2× better than others.',
    cta: 'Build this Creative',
  },
  {
    metric: 'VCR',
    body: 'The Social to CTV template is the best performing to drive engagement — up to 20% better VCR.',
    cta: 'Build this Creative',
  },
];

const RANK_STYLES = [
  'bg-primary text-white',
  'bg-primary-container text-primary',
  'bg-surface-container-high text-on-surface-variant',
];

const STATUS_PILL: Record<string, string> = {
  Active: 'bg-primary/10 text-primary',
  Scheduled: 'bg-on-surface-variant/10 text-on-surface-variant',
};

export default function AnalyticsPage() {
  return (
    <div className="p-4 md:p-8 lg:p-12 space-y-10">
      {/* Page header */}
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <h1 className="text-4xl font-black font-headline text-on-surface tracking-tight">
            Performance Analytics
          </h1>
          <p className="text-on-surface-variant mt-1 font-bold">
            Real-time cross-channel creative intelligence
          </p>
        </div>
        <div className="flex space-x-2">
          <button className="px-4 py-2 bg-surface-container-low text-on-surface-variant rounded-xl text-sm font-black hover:bg-surface-container-high transition-colors cursor-pointer">
            Last 30 Days
          </button>
          <button className="px-4 py-2 bg-primary text-on-primary rounded-xl text-sm font-black shadow-sm hover:bg-primary-dim transition-all flex items-center space-x-2 cursor-pointer">
            <span className="material-symbols-outlined text-sm">download</span>
            <span>Export Report</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {KPI_CARDS.map((card) => (
          <div
            key={card.label}
            className="bg-surface-container-lowest p-6 rounded-2xl border border-outline-variant/10 shadow-sm flex flex-col justify-between"
          >
            <div className="flex justify-between items-start">
              <span className="p-2 bg-primary-container text-primary rounded-lg">
                <span className="material-symbols-outlined">{card.icon}</span>
              </span>
              <span className="text-primary font-black text-xs">{card.delta}</span>
            </div>
            <div className="mt-4">
              <p className="text-xs text-on-surface-variant font-black uppercase tracking-wider">
                {card.label}
              </p>
              <p className="text-3xl font-headline font-black text-on-surface">{card.value}</p>
            </div>
          </div>
        ))}
      </section>

      {/* Main grid: performance table + AI insights | leaderboard */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left column (2/3) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Campaign performance table */}
          <div className="bg-surface-container-lowest rounded-2xl shadow-sm overflow-hidden border border-outline-variant/10">
            <div className="px-6 py-4 bg-surface-container-low border-b border-outline-variant/10 flex justify-between items-center">
              <h3 className="font-headline font-black text-on-surface">Campaign Performance</h3>
              <button className="text-primary text-xs font-black uppercase tracking-wider cursor-pointer hover:underline">
                View All
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-surface-container-low/50">
                  <tr>
                    {['Campaign', 'Status', 'QR Scans', 'VCR'].map((h) => (
                      <th
                        key={h}
                        className="px-6 py-3 text-[10px] font-black text-on-surface-variant uppercase tracking-widest"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/10">
                  {CAMPAIGN_ROWS.map((row) => (
                    <tr key={row.name} className="hover:bg-primary-container/30 transition-colors">
                      <td className="px-6 py-4 font-bold text-sm">{row.name}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 text-[10px] font-black rounded-full uppercase ${STATUS_PILL[row.status]}`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-bold">{row.qr}</td>
                      <td className="px-6 py-4 text-sm font-black text-primary">{row.vcr}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* AI Insights */}
          <div className="bg-primary rounded-2xl p-8 relative overflow-hidden shadow-xl text-on-primary">
            <div className="relative z-10 space-y-4">
              <div className="flex items-center space-x-2">
                <span className="material-symbols-outlined">auto_awesome</span>
                <h3 className="font-headline font-black text-lg">AI Optimisation Insights</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {AI_INSIGHTS.map((insight) => (
                  <div
                    key={insight.metric}
                    className="bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/20 flex flex-col"
                  >
                    <p className="text-[10px] font-black uppercase tracking-tighter text-primary-fixed mb-1">
                      {insight.metric}
                    </p>
                    <p className="text-sm font-medium leading-relaxed flex-grow">{insight.body}</p>
                    <button className="mt-4 w-full bg-white text-primary py-2 rounded-lg font-black text-sm hover:bg-primary-container transition-all cursor-pointer">
                      {insight.cta}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right column (1/3) — Creative Leaderboard */}
        <div className="flex flex-col h-full">
          <div className="bg-surface-container-lowest rounded-2xl shadow-sm p-6 flex-grow flex flex-col border border-outline-variant/10">
            <h3 className="font-headline font-black text-on-surface mb-6 flex items-center space-x-2">
              <span className="material-symbols-outlined text-primary">military_tech</span>
              <span>Creative Leaderboard</span>
            </h3>
            <div className="space-y-6 flex-grow">
              {LEADERBOARD.map((item, idx) => (
                <div key={item.name} className="flex items-center space-x-4">
                  <div className="relative shrink-0">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary-container to-surface-container-low flex items-center justify-center">
                      <span className="material-symbols-outlined text-primary/40 text-2xl">
                        image
                      </span>
                    </div>
                    <div
                      className={`absolute -top-2 -left-2 w-6 h-6 text-[10px] font-black rounded-full flex items-center justify-center ${RANK_STYLES[idx]}`}
                    >
                      {item.rank}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black text-on-surface truncate">{item.name}</p>
                    <p className="text-[10px] font-bold text-on-surface-variant">
                      {item.scans} QR Scans
                    </p>
                    <div className="w-full bg-surface-container-high h-1.5 rounded-full mt-2 overflow-hidden">
                      <div
                        className="bg-primary h-full rounded-full"
                        style={{ width: `${item.pct}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
